import { motion } from 'framer-motion';
import { useEffect, useState, useRef, useCallback } from 'react';
import type { RestCard as RestCardType } from '../api/workoutCards';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { speak } from '../../../lib/textToSpeech';
import { hapticCountdown, hapticSuccess, hapticNotification } from '../../../lib/haptics';
import {
  getRestTimerPreferences,
  saveRestTimerPreferences,
  formatRestTime,
  getMovementTypeLabel,
  type RestTimerPreferences,
} from '../../../lib/restTimerPreferences';
import {
  playCountdownBeep,
  playRestComplete,
  initializeAudio,
  isAudioAvailable,
} from '../../../lib/restTimerAudio';

interface RestCardProps {
  card: RestCardType;
  isPlaying: boolean;
  onSkip: () => void;
  ttsEnabled?: boolean;
  remainingTime?: number;
  onTimeUpdate?: (seconds: number) => void;
}

export function RestCard({
  card,
  isPlaying,
  onSkip,
  ttsEnabled = false,
  remainingTime,
  onTimeUpdate,
}: RestCardProps) {
  const [preferences, setPreferences] = useState<RestTimerPreferences>(getRestTimerPreferences);
  const [remaining, setRemaining] = useState(remainingTime ?? card.restSeconds);
  const [showSettings, setShowSettings] = useState(false);
  const hasSpokenRef = useRef<Set<number>>(new Set());
  const hasPlayedBeepRef = useRef<Set<number>>(new Set());
  const hasCompletedRef = useRef(false);

  // Get movement type from the card (passed through from exercise)
  const movementType = card.movementPattern;
  const movementLabel = getMovementTypeLabel(movementType);

  // Reset state when card changes
  useEffect(() => {
    // Only reset if we're coming to this card fresh (remainingTime not provided)
    if (remainingTime === undefined) {
      setRemaining(card.restSeconds);
      hasSpokenRef.current.clear();
      hasPlayedBeepRef.current.clear();
      hasCompletedRef.current = false;
    }
  }, [card.restSeconds, card.id, remainingTime]);

  // Initialize audio on mount (needs user interaction first)
  useEffect(() => {
    initializeAudio();
  }, []);

  // Handle timer completion
  const handleComplete = useCallback(() => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;

    // Play completion sound if enabled
    if (preferences.audioEnabled && preferences.completionSoundEnabled) {
      playRestComplete();
    }

    // Vibration feedback if enabled
    if (preferences.vibrationEnabled) {
      hapticSuccess();
    }

    // Auto-advance
    onSkip();
  }, [preferences.audioEnabled, preferences.completionSoundEnabled, preferences.vibrationEnabled, onSkip]);

  // Timer effect
  useEffect(() => {
    if (!isPlaying) return;

    const timer = setInterval(() => {
      setRemaining((prev) => {
        const next = prev - 1;

        // Notify parent of time update
        if (onTimeUpdate) {
          onTimeUpdate(next);
        }

        // Voice countdown for last 5 seconds
        if (ttsEnabled && next > 0 && next <= 5 && !hasSpokenRef.current.has(next)) {
          speak(next.toString(), { rate: 1.2 });
          hasSpokenRef.current.add(next);
        }

        // Audio beep countdown for last 3 seconds
        if (
          preferences.audioEnabled &&
          preferences.countdownBeepEnabled &&
          next > 0 &&
          next <= 3 &&
          !hasPlayedBeepRef.current.has(next)
        ) {
          playCountdownBeep(next);
          hasPlayedBeepRef.current.add(next);
        }

        // Haptic feedback for countdown
        if (preferences.vibrationEnabled && next > 0 && next <= 5) {
          hapticCountdown();
        }

        if (next <= 0) {
          return 0;
        }

        return next;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isPlaying, ttsEnabled, onTimeUpdate, preferences]);

  // Check for completion
  useEffect(() => {
    if (remaining <= 0 && isPlaying) {
      handleComplete();
    }
  }, [remaining, isPlaying, handleComplete]);

  // Adjust time handlers
  const addTime = (seconds: number) => {
    setRemaining((prev) => {
      const next = Math.max(0, prev + seconds);
      onTimeUpdate?.(next);
      return next;
    });
    // Initialize audio on user interaction
    initializeAudio();
  };

  // Update preferences
  const updatePreference = <K extends keyof RestTimerPreferences>(
    key: K,
    value: RestTimerPreferences[K]
  ) => {
    const updated = saveRestTimerPreferences({ [key]: value });
    setPreferences(updated);
    // Initialize audio on user interaction
    initializeAudio();
  };

  // Skip rest immediately
  const handleSkip = () => {
    // Initialize audio on user interaction
    initializeAudio();
    // Provide haptic feedback
    if (preferences.vibrationEnabled) {
      hapticNotification();
    }
    onSkip();
  };

  const progress = (remaining / card.restSeconds) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      className="h-full"
    >
      <Card padding="lg" variant="elevated" className="h-full flex flex-col justify-between space-y-4">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2">
            <Badge variant="info" size="md">Rest Period</Badge>
            <Badge variant="default" size="sm">{movementLabel}</Badge>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            {card.exerciseName}
          </h3>

          <p className="text-sm text-gray-600 dark:text-slate-400">
            After set {card.afterSet} • Recommended: {formatRestTime(card.restSeconds)}
          </p>
        </div>

        {/* Circular Timer */}
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="relative w-44 h-44 sm:w-52 sm:h-52">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 200 200">
              <circle
                cx="100"
                cy="100"
                r="90"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-gray-200 dark:text-white/10"
              />
              <motion.circle
                cx="100"
                cy="100"
                r="90"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 90}`}
                strokeDashoffset={`${2 * Math.PI * 90 * (1 - progress / 100)}`}
                strokeLinecap="round"
                className={remaining <= 5 ? 'text-amber-500' : 'text-primary-500'}
                animate={{ strokeDashoffset: `${2 * Math.PI * 90 * (1 - progress / 100)}` }}
                transition={{ duration: 1, ease: 'linear' }}
              />
            </svg>

            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <p className={`text-5xl sm:text-6xl font-bold font-mono ${
                remaining <= 5 ? 'text-amber-500' : 'text-gray-900 dark:text-white'
              }`}>
                {remaining}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 uppercase tracking-wide">
                seconds
              </p>
            </div>
          </div>

          {/* Time adjustment buttons */}
          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={() => addTime(-15)}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/20 transition"
            >
              -15s
            </button>
            <button
              onClick={() => addTime(-30)}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/20 transition"
            >
              -30s
            </button>
            <button
              onClick={() => addTime(30)}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/20 transition"
            >
              +30s
            </button>
            <button
              onClick={() => addTime(60)}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/20 transition"
            >
              +1m
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          {/* Skip Rest Button - Primary action */}
          <Button
            variant="primary"
            size="lg"
            onClick={handleSkip}
            className="w-full text-lg"
          >
            Skip Rest →
          </Button>

          {/* Secondary actions row */}
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setRemaining(card.restSeconds);
                hasPlayedBeepRef.current.clear();
                hasCompletedRef.current = false;
                onTimeUpdate?.(card.restSeconds);
                initializeAudio();
              }}
              className="flex-1"
            >
              Reset Timer
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowSettings(!showSettings);
                initializeAudio();
              }}
              className="flex-1"
            >
              {showSettings ? 'Hide Settings' : 'Settings'}
            </Button>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="p-4 bg-gray-50 dark:bg-white/5 rounded-lg space-y-4"
            >
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Rest Timer Settings
              </h4>

              {/* Audio toggle */}
              {isAudioAvailable() && (
                <label className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-slate-300">Sound notifications</span>
                  <input
                    type="checkbox"
                    checked={preferences.audioEnabled}
                    onChange={(e) => updatePreference('audioEnabled', e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                  />
                </label>
              )}

              {/* Vibration toggle */}
              <label className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-slate-300">Vibration</span>
                <input
                  type="checkbox"
                  checked={preferences.vibrationEnabled}
                  onChange={(e) => updatePreference('vibrationEnabled', e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                />
              </label>

              {/* Countdown beep toggle (only if audio enabled) */}
              {preferences.audioEnabled && (
                <label className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-slate-300">Countdown beeps (3, 2, 1)</span>
                  <input
                    type="checkbox"
                    checked={preferences.countdownBeepEnabled}
                    onChange={(e) => updatePreference('countdownBeepEnabled', e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                  />
                </label>
              )}

              {/* Default rest times */}
              <div className="pt-2 border-t border-gray-200 dark:border-white/10 space-y-3">
                <p className="text-xs text-gray-500 dark:text-slate-500">Default rest times (affects new workouts)</p>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-slate-300">Compound exercises</span>
                  <select
                    value={preferences.compoundRestTime}
                    onChange={(e) => updatePreference('compoundRestTime', Number(e.target.value))}
                    className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 text-gray-900 dark:text-white"
                  >
                    <option value={60}>1:00</option>
                    <option value={90}>1:30</option>
                    <option value={120}>2:00</option>
                    <option value={150}>2:30</option>
                    <option value={180}>3:00</option>
                  </select>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-slate-300">Isolation exercises</span>
                  <select
                    value={preferences.isolationRestTime}
                    onChange={(e) => updatePreference('isolationRestTime', Number(e.target.value))}
                    className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 text-gray-900 dark:text-white"
                  >
                    <option value={30}>0:30</option>
                    <option value={45}>0:45</option>
                    <option value={60}>1:00</option>
                    <option value={90}>1:30</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </Card>
    </motion.div>
  );
}
